import { useForm } from 'react-hook-form';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    List,
    Skeleton,
    styled,
    Typography
} from '@mui/material';
import { Match } from 'effect';

import { formatFlagKey } from '../../helpers/FormattingHelpers';
import type { FlagKey } from '../../types/Flag';
import type { LoggerId } from '../../types/Logger';

const TitleContainer = styled('div')`
    display: flex;
    flex-direction: column;
    padding: ${({ theme }) => theme.spacing(3)};
`;

const FlagContainer = styled(List)`
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
`;

type FlagState = {
    key: FlagKey;
    active: boolean;
    disabled: boolean;
};

const isFlagActive = (flag: FlagState): boolean => flag.active;

const getFlagKey = (flag: FlagState): FlagKey => flag.key;

export type FlagAsDamagedModalProps = {
    loggerId: LoggerId;
    flags: Array<FlagState>;
    onClose: () => void;
    onSubmit: (flags: Array<FlagKey>) => void;
    isLoading: boolean;
};

// TODO: Remove/Replace all pixel values in this file with rem values when we've removed the font-size
// TODO: variable set on the html root element in Global.less since it messes with all rem values

export const FlagAsDamagedModal = ({
    onClose,
    loggerId,
    onSubmit,
    flags: _flags,
    isLoading
}: FlagAsDamagedModalProps) => {
    const form = useForm({
        values: {
            flags: _flags
        }
    });

    const flags = form.watch('flags');

    const onFlagClick = (flag: FlagState) => {
        form.setValue(
            'flags',
            flags.map(
                (prevFlag) =>
                    ({
                        ...prevFlag,
                        active: prevFlag.key === flag.key ? !prevFlag.active : prevFlag.active
                    }) satisfies FlagState
            )
        );
    };

    const handleSubmit = form.handleSubmit((data) => {
        onSubmit(data.flags.filter(isFlagActive).map(getFlagKey));
    });

    // Submit button should be disabled if the flags are the same as the initial flags
    const isDisabled = isLoading || _flags.filter(isFlagActive).length === flags.filter(isFlagActive).length;

    return (
        <Dialog open PaperProps={{ sx: { width: '500px', borderRadius: '6px' } }} onClose={onClose}>
            <form onSubmit={handleSubmit}>
                <TitleContainer>
                    <DialogTitle sx={{ padding: '0', fontWeight: 500, fontSize: '22px', color: '#004274' }}>
                        Flag as damaged
                    </DialogTitle>

                    <Typography variant="subtitle2" sx={{ color: 'GrayText', fontSize: '14px' }}>
                        Logger {loggerId}
                    </Typography>
                </TitleContainer>

                <Divider />

                <DialogContent sx={{ padding: '24px' }}>
                    <FlagContainer>{flagMatcher({ flags, isLoading, onFlagClick })}</FlagContainer>
                </DialogContent>

                <Divider />

                <DialogActions sx={{ justifyContent: 'space-between', padding: '24px' }}>
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>

                    <Button variant="contained" type="submit" disabled={isDisabled}>
                        Confirm
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

const CheckboxSkeleton = () => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Skeleton variant="rounded" width={24} height={24} />

            <Skeleton variant="rounded" height={24} width="250px" />
        </Box>
    );
};

const flagMatcher = Match.type<{
    flags: Array<FlagState>;
    isLoading: boolean;
    onFlagClick: (flag: FlagState) => void;
}>().pipe(
    Match.when(
        ({ isLoading }) => isLoading,
        () => (
            <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid="skeleton-loader">
                {Array.from({ length: 5 }).map((_, index) => (
                    <CheckboxSkeleton key={index} />
                ))}
            </Box>
        )
    ),
    Match.when(
        ({ flags }) => flags.length === 0,
        () => (
            <Typography variant="body1" sx={{ fontSize: '16px' }}>
                No flags available
            </Typography>
        )
    ),
    Match.orElse(({ flags, onFlagClick }) => (
        <>
            {flags.map((flag) => (
                <FormControlLabel
                    key={flag.key}
                    label={formatFlagKey(flag.key)}
                    slotProps={{
                        typography: {
                            sx: {
                                fontSize: '16px'
                            }
                        }
                    }}
                    control={
                        <Checkbox
                            checked={flag.active}
                            disabled={flag.disabled}
                            onClick={() => onFlagClick(flag)}
                            icon={
                                <CheckBoxOutlineBlankIcon
                                    // Force the size of the icon. Our global.less is messing with the size of the icon via it's font-size
                                    sx={{ fontSize: '24px', padding: 0 }}
                                />
                            }
                            checkedIcon={
                                <CheckBoxIcon
                                    // Force the size of the icon. Our global.less is messing with the size of the icon via it's font-size
                                    sx={{ fontSize: '24px', padding: 0 }}
                                />
                            }
                        />
                    }
                />
            ))}
        </>
    ))
);
